import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import TooltipOption from "../../../core/common/tooltipOption";
import { message, TimePicker } from "antd";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";
import {
  getAllShifts,
  updateShift,
  Shift,
  DayConfig,
} from "../../../service/shift";
import { getAllClasses, Class } from "../../../service/classes";

const DAYS_ORDER: DayConfig["day"][] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const Shifts: React.FC = () => {
  const componentRef = useRef<HTMLDivElement>(null);
  const currentBranch = useSelector(selectCurrentBranch);

  const [allShifts, setAllShifts] = useState<Shift[]>([]);
  const [filteredShifts, setFilteredShifts] = useState<Shift[]>([]);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [originalConfig, setOriginalConfig] = useState<Record<string, DayConfig>>({}); // ← Original data for comparison
  const [dayConfig, setDayConfig] = useState<Record<string, DayConfig>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const [tempFilter, setTempFilter] = useState<"student" | "employee" | "">("");

  // Which classes the selected shift applies to. Empty = the classless
  // default shift for everyone (matches findStudentShift's fallback on the
  // backend). Only meaningful for student/both shifts.
  const [classOptions, setClassOptions] = useState<Class[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [originalClassIds, setOriginalClassIds] = useState<string[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
  });

  const handleExportExcel = () => {
    if (!selectedShift) {
      toast.info("Please select a shift to export");
      return;
    }

    const data = DAYS_ORDER.map((day) => {
      const cfg = dayConfig[day];
      return {
        Day: day.charAt(0).toUpperCase() + day.slice(1),
        "Check In": cfg.checkIn || "Off",
        "Check Out": cfg.checkOut || "Off",
        "Late After": cfg.lateAfter || "Off",
        "Min Hours": cfg.minimumHours || "Off",
        "Half Day After": cfg.halfDayAfter || "Off",
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Shift Timings");
    XLSX.writeFile(wb, `${selectedShift.title.replace(/[^a-z0-9]/gi, '_')}_timings.xlsx`);
    toast.success("Shift timings exported!");
  };

  const fetchShiftsData = async (appliedFilter = tempFilter) => {
    try {
      const response = await getAllShifts({ limit: 100 });
      if (response.success && response.data.shifts.length > 0) {
        const shiftsList = response.data.shifts;
        setAllShifts(shiftsList);

        let filtered = shiftsList;
        if (appliedFilter) {
          filtered = shiftsList.filter((s) => s.applicableTo === appliedFilter);
        }
        setFilteredShifts(filtered);

        let defaultShift: Shift | undefined;
        if (appliedFilter === "student") {
          defaultShift = filtered.find((s) => s.applicableTo === "student");
        } else if (appliedFilter === "employee") {
          defaultShift = filtered.find((s) => s.applicableTo === "employee");
        } else {
          defaultShift = filtered.find((s) => s.applicableTo === "student") || filtered[0];
        }

        setSelectedShift(defaultShift || null);
        if (defaultShift) {
          loadDayConfig(defaultShift);
        } else {
          setDayConfig({});
          setOriginalConfig({});
        }
      } else {
        setAllShifts([]);
        setFilteredShifts([]);
        setSelectedShift(null);
        setDayConfig({});
        setOriginalConfig({});
      }
    } catch (error) {
      toast.error("Failed to load shifts");
      setAllShifts([]);
      setFilteredShifts([]);
      setSelectedShift(null);
      setDayConfig({});
      setOriginalConfig({});
    }
  };

  useEffect(() => {
    const initialFetch = async () => {
      setLoading(true);
      await fetchShiftsData();
      setLoading(false);
    };
    initialFetch();
  }, []);

  const loadDayConfig = (shift: Shift) => {
    const configMap: Record<string, DayConfig> = {};
    shift.dayWiseConfig.forEach((cfg) => {
      configMap[cfg.day] = {
        day: cfg.day,
        checkIn: cfg.checkIn || "",
        checkOut: cfg.checkOut || "",
        lateAfter: cfg.lateAfter || "",
        minimumHours: cfg.minimumHours ?? 0,
        halfDayAfter: cfg.halfDayAfter || "",
      };
    });

    const fullConfig: Record<string, DayConfig> = {};
    DAYS_ORDER.forEach((day) => {
      fullConfig[day] = configMap[day] || {
        day,
        checkIn: "",
        checkOut: "",
        lateAfter: "",
        minimumHours: 0,
        halfDayAfter: "",
      };
    });

    setDayConfig(fullConfig);
    setOriginalConfig(JSON.parse(JSON.stringify(fullConfig))); // Deep copy for comparison

    const classIds = (shift.classes || []).map((c) => c._id);
    setSelectedClassIds(classIds);
    setOriginalClassIds(classIds);

    // Class options are scoped to this shift's own System -- Classes are a
    // shared catalog keyed by System, not by Branch/Session.
    const systemId = shift.system?._id;
    if (systemId) {
      setClassesLoading(true);
      getAllClasses({ system: systemId, limit: 200 })
        .then((res) => setClassOptions(res.classes || []))
        .catch(() => setClassOptions([]))
        .finally(() => setClassesLoading(false));
    } else {
      setClassOptions([]);
    }
  };

  const toggleClassId = (classId: string) => {
    setSelectedClassIds((prev) =>
      prev.includes(classId) ? prev.filter((id) => id !== classId) : [...prev, classId]
    );
  };

  const handleShiftChange = (shiftId: string) => {
    const shift = filteredShifts.find((s) => s._id === shiftId);
    if (shift) {
      setSelectedShift(shift);
      loadDayConfig(shift);
    }
  };

  const updateDayTime = (
    day: string,
    field: "checkIn" | "checkOut" | "lateAfter" | "halfDayAfter",
    time: dayjs.Dayjs | null
  ) => {
    if (day === "sunday") return;

    const timeStr = time ? time.format("HH:mm") : "";

    setDayConfig((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: timeStr,
      },
    }));
  };

  const updateDayField = (day: string, field: "minimumHours", value: string) => {
    if (day === "sunday") return;

    setDayConfig((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: Number(value) || 0,
      },
    }));
  };

  const isOffDay = (cfg: DayConfig) => {
    return (
      !cfg.checkIn &&
      !cfg.checkOut &&
      !cfg.lateAfter &&
      !cfg.halfDayAfter &&
      cfg.minimumHours === 0
    );
  };

  // Check if there are any changes
  const hasChanges = () => {
    if (!selectedShift) return false;

    for (const day of DAYS_ORDER) {
      if (day === "sunday") continue;

      const original = originalConfig[day];
      const current = dayConfig[day];

      if (
        original.checkIn !== current.checkIn ||
        original.checkOut !== current.checkOut ||
        original.lateAfter !== current.lateAfter ||
        original.minimumHours !== current.minimumHours ||
        original.halfDayAfter !== current.halfDayAfter
      ) {
        return true;
      }
    }

    if (selectedClassIds.length !== originalClassIds.length) return true;
    const originalSet = new Set(originalClassIds);
    if (selectedClassIds.some((id) => !originalSet.has(id))) return true;

    return false;
  };

  const saveChanges = async () => {
    if (!selectedShift || !hasChanges()) return;

    const updatedDayWiseConfig: DayConfig[] = DAYS_ORDER.map((day) => ({
      ...dayConfig[day],
      minimumHours: dayConfig[day].minimumHours ?? 0,
    }))
      .filter((cfg) => cfg.day !== "sunday")
      .filter((cfg) => cfg.checkIn || cfg.checkOut);

    try {
      await updateShift(selectedShift._id, {
        dayWiseConfig: updatedDayWiseConfig,
        classes: selectedClassIds,
      });
      toast.success("Shift timings saved successfully!");
      // Update original config after save
      setOriginalConfig(JSON.parse(JSON.stringify(dayConfig)));
      setOriginalClassIds(selectedClassIds);
    } catch (error) {
      toast.error("Failed to save timings");
    }
  };

  const handleApplyFilter = () => {
    setTableLoading(true);

    setTimeout(() => {
      let filtered = allShifts;

      if (tempFilter) {
        filtered = allShifts.filter((s) => s.applicableTo === tempFilter);
      }

      setFilteredShifts(filtered);

      if (filtered.length > 0) {
        const newSelected = filtered[0];
        setSelectedShift(newSelected);
        loadDayConfig(newSelected);
      } else {
        setSelectedShift(null);
        setDayConfig({});
        setOriginalConfig({});
      }

      setTableLoading(false);

      if (dropdownMenuRef.current) {
        dropdownMenuRef.current.classList.remove("show");
      }
    }, 300);
  };

  const handleResetFilter = () => {
    setTempFilter("");
    setFilteredShifts(allShifts);

    const defaultShift =
      allShifts.find((s) => s.applicableTo === "student") ||
      allShifts[0];

    setSelectedShift(defaultShift);
    if (defaultShift) {
      loadDayConfig(defaultShift);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchShiftsData(tempFilter);
    setRefreshing(false);
    message.success("Data refreshed successfully");
  };

  return (
    <div className="page-wrapper">
      <div className="content printable-content" ref={componentRef}>
        <div className="print-only-header">
          <div className="school-brand">
            <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
            <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
          </div>
          <div className="report-title">Shifts List</div>
          <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
        </div>
        {(loading || refreshing) && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 z-1050">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Shift Configuration</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="#">Settings</a></li>
                <li className="breadcrumb-item active">Shift Timings</li>
              </ol>
            </nav>
          </div>

          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <TooltipOption
              onRefresh={handleRefresh}
              onExportPdf={handlePrint}
              onExportExcel={handleExportExcel}
            />

            <div className="dropdown mb-3 me-2">
              <button
                className="btn btn-outline-light bg-white dropdown-toggle"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
              >
                <i className="ti ti-filter me-2"></i>
                Filter
              </button>
              <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                <div className="d-flex align-items-center border-bottom p-3">
                  <h4>Filter Shifts</h4>
                </div>
                <div className="p-3">
                  <label className="form-label">Applicable To</label>
                  <select
                    className="form-select"
                    value={tempFilter}
                    onChange={(e) => setTempFilter(e.target.value as any)}
                  >
                    <option value="student">Students</option>
                    <option value="employee">Employees</option>
                  </select>
                </div>
                <div className="p-3 border-top d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-light" onClick={handleResetFilter}>
                    Reset
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleApplyFilter}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              Day-wise Timings —{" "}
              {selectedShift?.title || "No Shift Selected"}{" "}
              {selectedShift && (
                <small className="text-muted">
                  ({selectedShift.applicableTo === "student" ? "Students" : selectedShift.applicableTo === "employee" ? "Employees" : "Both"})
                </small>
              )}
            </h4>
            {/* Save Changes Button – Blue color like Apply Filter, disabled if no changes */}
            <button
              className="btn btn-primary btn-sm" // ← Changed from btn-success to btn-primary
              onClick={saveChanges}
              disabled={!selectedShift || !hasChanges()}
            >
              <i className="ti ti-device-floppy me-2"></i>
              {hasChanges() ? "Save Changes" : "No Changes"}
            </button>
          </div>

          {filteredShifts.length > 1 && (
            <div className="card-body border-bottom py-3">
              <label className="form-label fw-semibold mb-1">Shift</label>
              <select
                className="form-select"
                style={{ maxWidth: 360 }}
                value={selectedShift?._id || ""}
                onChange={(e) => handleShiftChange(e.target.value)}
              >
                {filteredShifts.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="card-body p-0">
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading shifts...</p>
              </div>
            ) : tableLoading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Applying filter...</p>
              </div>
            ) : !selectedShift ? (
              <div className="text-center p-5 text-muted">
                <i className="ti ti-calendar-off fs-48 mb-3"></i>
                <h5>No Shift Available</h5>
                <p>Select a shift from the dropdown above.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-borderless mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "150px" }}>Day</th>
                      <th style={{ width: "180px" }}>Check In</th>
                      <th style={{ width: "180px" }}>Check Out</th>
                      <th style={{ width: "180px" }}>Late After</th>
                      <th style={{ width: "120px" }}>Min Hours</th>
                      <th style={{ width: "180px" }}>Half Day After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DAYS_ORDER.map((day) => {
                      const cfg = dayConfig[day];
                      const isOff = isOffDay(cfg);

                      return (
                        <tr key={day} className={isOff ? "bg-light" : ""}>
                          <td className="text-capitalize fw-medium">
                            {day}
                            {isOff && (
                              <span className="badge badge-soft-secondary ms-2 small">
                                Off Day
                              </span>
                            )}
                          </td>

                          <td>
                            <TimePicker
                              format="HH:mm"
                              value={cfg.checkIn ? dayjs(cfg.checkIn, "HH:mm") : null}
                              onChange={(time) => updateDayTime(day, "checkIn", time)}
                              placeholder={isOff ? "Off" : "08:00"}
                              disabled={isOff}
                              style={{ width: "100%" }}
                              allowClear={false}
                            />
                          </td>

                          <td>
                            <TimePicker
                              format="HH:mm"
                              value={cfg.checkOut ? dayjs(cfg.checkOut, "HH:mm") : null}
                              onChange={(time) => updateDayTime(day, "checkOut", time)}
                              placeholder={isOff ? "Off" : "14:00"}
                              disabled={isOff}
                              style={{ width: "100%" }}
                              allowClear={false}
                            />
                          </td>

                          <td>
                            <TimePicker
                              format="HH:mm"
                              value={cfg.lateAfter ? dayjs(cfg.lateAfter, "HH:mm") : null}
                              onChange={(time) => updateDayTime(day, "lateAfter", time)}
                              placeholder={isOff ? "Off" : "08:15"}
                              disabled={isOff}
                              style={{ width: "100%" }}
                              allowClear={false}
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              className="form-control text-center"
                              value={cfg.minimumHours || ""}
                              onChange={(e) =>
                                updateDayField(day, "minimumHours", e.target.value)
                              }
                              min="0"
                              step="0.5"
                              placeholder={isOff ? "Off" : "5"}
                              disabled={isOff}
                            />
                          </td>

                          <td>
                            <TimePicker
                              format="HH:mm"
                              value={cfg.halfDayAfter ? dayjs(cfg.halfDayAfter, "HH:mm") : null}
                              onChange={(time) => updateDayTime(day, "halfDayAfter", time)}
                              placeholder={isOff ? "Off" : "11:00"}
                              disabled={isOff}
                              style={{ width: "100%" }}
                              allowClear={false}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Which classes this shift applies to -- only meaningful for
              student/both shifts. Leaving every box unticked means this is
              the classless default shift everyone else falls back to
              (findStudentShift's fallback on the backend), same as before
              this picker existed. */}
          {selectedShift && selectedShift.applicableTo !== "employee" && (
            <div className="card-body border-top">
              <label className="form-label fw-semibold mb-1">
                Applies to classes <span className="text-muted fw-normal">(none ticked = default shift for every class not covered by a more specific one)</span>
              </label>
              {classesLoading ? (
                <p className="text-muted small mb-0">Loading classes…</p>
              ) : classOptions.length === 0 ? (
                <p className="text-muted small mb-0">No classes found for this shift's System.</p>
              ) : (
                <div className="row">
                  {classOptions.map((cls) => (
                    <div className="col-md-3 col-6 mb-2" key={cls._id}>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedClassIds.includes(cls._id)}
                          onChange={() => toggleClassId(cls._id)}
                          id={`shift-class-${cls._id}`}
                        />
                        <label className="form-check-label" htmlFor={`shift-class-${cls._id}`}>
                          {cls.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Save All Changes Button – Same blue color */}
          {!loading && !refreshing && !tableLoading && selectedShift && (
            <div className="card-footer bg-transparent text-end">
              <button
                className="btn btn-primary" // ← Blue like Apply Filter
                onClick={saveChanges}
                disabled={!hasChanges()}
              >
                <i className="ti ti-device-floppy me-2"></i>
                {hasChanges() ? "Save All Changes" : "No Changes to Save"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shifts;